import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Hook for subscribing to an image refinement job's real-time updates
 *
 * @param {string|null} jobId - The job ID to subscribe to
 * @returns {{ job: Object|null, loading: boolean, error: string|null }}
 */
export function useImageRefinementJob(jobId) {
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!jobId) {
            setJob(null);
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        console.log(`[${new Date().toISOString()}] 🔗 Subscribing to imageRefinementJobs/`, jobId);

        // Subscribe to the job document
        const unsubscribe = onSnapshot(
            doc(db, 'imageRefinementJobs', jobId),
            (snapshot) => {
                if (snapshot.exists()) {
                    const data = { id: snapshot.id, ...snapshot.data() };
                    console.log(`[${new Date().toISOString()}] 📥 Job snapshot received:`, { id: data.id, status: data.status, progress: data.progress });
                    setJob(data);
                } else {
                    console.warn(`[${new Date().toISOString()}] ⚠️ Job document does not exist:`, jobId);
                    setJob(null);
                    setError('Job not found');
                }
                setLoading(false);
            },
            (err) => {
                console.error(`[${new Date().toISOString()}] ❌ Error subscribing to image refinement job:`, err);
                setError(err.message);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [jobId]);

    return { job, loading, error };
}

/**
 * Service functions for image refinement jobs
 */

const SERVER_URL = 'http://localhost:3001';

/**
 * Start a background image refinement job
 * Returns immediately with jobId - refinement continues on server
 *
 * @param {Object} options
 * @param {string} options.userId - User ID
 * @param {string} options.brandId - Brand ID
 * @param {string} options.originalImageUrl - URL of the original image
 * @param {string} options.refinementPrompt - The prompt for refinement
 * @param {Array} options.referenceImages - Reference images array
 * @param {string} options.aspectRatio - Aspect ratio (default '1:1')
 * @param {string} options.libraryItemId - Optional library item ID to update
 * @returns {Promise<{jobId: string, status: string, message: string}>}
 */
export async function startImageRefinement({
    userId,
    brandId,
    originalImageUrl,
    refinementPrompt,
    referenceImages = [],
    aspectRatio,
    libraryItemId = null
}) {
    console.log(`[${new Date().toISOString()}] 🚀 Calling /api/image-refinement/start...`);

    const response = await fetch(`${SERVER_URL}/api/image-refinement/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            userId,
            brandId,
            originalImageUrl,
            refinementPrompt,
            referenceImages,
            aspectRatio,
            libraryItemId
        })
    });

    console.log(`[${new Date().toISOString()}] 📡 Response status:`, response.status);

    if (!response.ok) {
        const error = await response.json();
        console.error(`[${new Date().toISOString()}] ❌ Start refinement failed:`, error);
        throw new Error(error.details || error.error || 'Failed to start image refinement');
    }

    const result = await response.json();
    console.log(`[${new Date().toISOString()}] ✅ Refinement job created:`, result);
    return result; // { jobId, status, message }
}

/**
 * Get job status from server (fallback if Firestore not working)
 */
export async function getRefinementJobStatus(jobId) {
    const response = await fetch(`${SERVER_URL}/api/image-refinement/${jobId}`);

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to get job status');
    }

    return response.json();
}

/**
 * Cancel a running refinement job
 */
export async function cancelRefinementJob(jobId) {
    const response = await fetch(`${SERVER_URL}/api/image-refinement/${jobId}/cancel`, {
        method: 'POST'
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to cancel job');
    }

    return response.json();
}

/**
 * Hook for tracking active image refinement jobs for a user
 * This can be used to show a global indicator of running jobs
 */
export function useActiveRefinementJobs(userId) {
    const [activeJobs, setActiveJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId) {
            setActiveJobs([]);
            setLoading(false);
            return;
        }

        const setupListeners = async () => {
            const { collection, query, where, orderBy, onSnapshot: firestoreOnSnapshot } = await import('firebase/firestore');

            // Active jobs (pending or processing)
            const activeQuery = query(
                collection(db, 'imageRefinementJobs'),
                where('userId', '==', userId),
                where('status', 'in', ['pending', 'processing']),
                orderBy('createdAt', 'desc')
            );

            const unsubscribe = firestoreOnSnapshot(activeQuery,
                (snapshot) => {
                    const jobs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setActiveJobs(jobs);
                    setLoading(false);
                },
                (error) => {
                    console.error(`[${new Date().toISOString()}] Error getting active refinement jobs:`, error);
                    setLoading(false);
                }
            );

            return unsubscribe;
        };

        let cleanup;
        setupListeners().then(cleanupFn => {
            cleanup = cleanupFn;
        });

        return () => {
            if (cleanup) cleanup();
        };
    }, [userId]);

    return { activeJobs, loading };
}

export default useImageRefinementJob;
